import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Clock3, ExternalLink, Link2, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { socialDisplayName, toExternalUrl, toTelHref, toWhatsAppHref, visibleContactItems, visibleSocialLinks } from "@/lib/contactSettings";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function Contact() {
  const { t } = useI18n();
  const { settings } = useSiteSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/requests", { ...form, request_type: "contact", source: "contact_form" });
      toast.success("Message envoyé à Team Work.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Impossible d'envoyer le message.");
    } finally {
      setSubmitting(false);
    }
  };

  const rows = [
    { id: "address", icon: MapPin, label: t("contact.address"), value: settings?.address, href: settings?.maps_url ? toExternalUrl(settings.maps_url) : null },
    ...visibleContactItems(settings?.phones, settings?.phone, "Téléphone").map((item, index) => ({ id: `phone-${index}`, icon: Phone, label: item.label || "Téléphone", value: item.value, href: toTelHref(item.value) })),
    ...visibleContactItems(settings?.emails, settings?.email, "E-mail").map((item, index) => ({ id: `email-${index}`, icon: Mail, label: item.label || "E-mail", value: item.value, href: `mailto:${item.value}` })),
    ...visibleContactItems(settings?.whatsapps, settings?.whatsapp, "WhatsApp").map((item, index) => ({ id: `whatsapp-${index}`, icon: MessageCircle, label: item.label || "WhatsApp", value: item.value, href: toWhatsAppHref(item.value) })),
    settings?.hours && { id: "hours", icon: Clock3, label: "Horaires", value: settings.hours },
    settings?.website_url && { id: "website", icon: ExternalLink, label: "Site web", value: settings.website_url, href: toExternalUrl(settings.website_url) },
  ].filter((r) => r && r.value);
  const socials = visibleSocialLinks(settings?.social_links);

  return (
    <div className="container-app py-8 lg:py-20">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="kicker">Contact</div>
          <h1 className="mt-2 font-display text-3xl lg:text-5xl text-brand-navy font-extrabold tracking-tight leading-tight">
            {t("contact.title")}
          </h1>
          <p className="mt-3 text-slate-600 max-w-md">{t("contact.subtitle")}</p>
          {settings?.contact_note && <p className="mt-4 max-w-md rounded-xl bg-brand-mist px-4 py-3 text-sm font-semibold text-brand-navy">{settings.contact_note}</p>}

          <div className="mt-8 space-y-4">
            {rows.length === 0 ? (
              <div className="rounded-xl bg-brand-mist p-4 text-sm text-slate-600">
                Utilisez le formulaire pour contacter Team Work.
              </div>
            ) : (
              rows.map((r) => {
                const Content = (
                  <div className="flex items-start gap-3">
                    <div className="h-11 w-11 rounded-xl bg-brand-orangeSoft flex items-center justify-center flex-shrink-0"><r.icon className="h-5 w-5 text-brand-orange" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{r.label}</div>
                      <div className="mt-0.5 text-brand-navy font-display text-base font-bold">{r.value}</div>
                    </div>
                  </div>
                );
                return r.href ? (
                  <a key={r.label} href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block hover:opacity-80 transition-opacity">{Content}</a>
                ) : (
                  <div key={r.label}>{Content}</div>
                );
              })
            )}
            {socials.length > 0 && (
              <div className="mt-6 border-t border-slate-100 pt-5">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Suivre Team Work</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {socials.map((social, index) => <a key={`${social.platform}-${index}`} href={toExternalUrl(social.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-brand-navy transition hover:border-brand-orange hover:text-brand-orange"><Link2 className="h-4 w-4" />{socialDisplayName(social.platform, social.label)}<ExternalLink className="h-3.5 w-3.5" /></a>)}
                </div>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="card-app p-6 lg:p-8 space-y-4" data-testid="contact-form">
          <div>
            <Label>Nom complet</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
            </div>
          </div>
          <div>
            <Label>Message</Label>
            <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="contact-message" />
          </div>
          <Button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="contact-submit">
            {submitting ? "Envoi…" : "Envoyer à Team Work"}
          </Button>
        </form>
      </div>
    </div>
  );
}
